import { useState, useMemo, useCallback, useEffect } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Layout, Menu, Button, Avatar, Dropdown, Typography, Space, Drawer, Grid, Modal, Spin, Alert } from 'antd'
import {
  DashboardOutlined,
  PlusCircleOutlined,
  HistoryOutlined,
  LogoutOutlined,
  UserOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  FileTextOutlined,
  ShoppingOutlined,
  DownOutlined,
  CustomerServiceOutlined,
  CalendarOutlined,
} from '@ant-design/icons'
import { logout } from '../../store/slices/authSlice'
import '../Task/TaskDetail.css'
import './MainLayout.css'
import { marked } from 'marked'
import SupportChat from '../SupportChat/SupportChat'

const { Header, Sider, Content } = Layout
const { Title } = Typography
const { useBreakpoint } = Grid

const MainLayout = () => {
  const [collapsed, setCollapsed] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [disclaimerVisible, setDisclaimerVisible] = useState(false)
  const [disclaimerContent, setDisclaimerContent] = useState('')
  const [disclaimerLoading, setDisclaimerLoading] = useState(false)
  const [disclaimerError, setDisclaimerError] = useState('')
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const { user } = useSelector((state) => state.auth)
  const screens = useBreakpoint()
  const isMobile = !screens.md

  const menuItems = useMemo(() => ([
    {
      key: '/dashboard',
      icon: <DashboardOutlined style={{ fontSize: 20 }} />,
      label: 'Home',
      style: { height: 56 },
    },
    {
      key: '/tasks/new',
      icon: <PlusCircleOutlined style={{ fontSize: 20 }} />,
      label: 'New Analysis',
      style: { height: 56 },
    },
    {
      key: '/scheduled-tasks',
      icon: <CalendarOutlined style={{ fontSize: 20 }} />,
      label: 'Subscription',
      style: { height: 56 },
    },
    {
      key: '/tasks',
      icon: <HistoryOutlined style={{ fontSize: 20 }} />,
      label: 'History',
      style: { height: 56 },
    },
  ]), [])

  const activeMenuKey = useMemo(() => {
    const matched = menuItems.find((item) => {
      if (location.pathname === item.key) {
        return true
      }
      return location.pathname.startsWith(`${item.key}/`)
    })
    return matched?.key || ''
  }, [location.pathname, menuItems])

  useEffect(() => {
    if (!isMobile) {
      setMobileMenuOpen(false)
    }
  }, [isMobile])

  const isTaskCreateRoute = useMemo(() => location.pathname.startsWith('/tasks/new'), [location.pathname])

  const handleDisclaimerOpen = useCallback(() => {
    setDisclaimerVisible(true)
    if (isMobile) {
      setMobileMenuOpen(false)
    }
  }, [isMobile])

  useEffect(() => {
    if (!disclaimerVisible || disclaimerContent || disclaimerLoading) {
      return
    }

    const loadDisclaimer = async () => {
      setDisclaimerLoading(true)
      setDisclaimerError('')
      try {
        const response = await fetch('/docs/terms.md', { cache: 'no-cache' })
        if (!response.ok) {
          throw new Error('Failed to load terms')
        }
        const text = await response.text()
        setDisclaimerContent(text)
      } catch (error) {
        console.error('Failed to fetch terms markdown', error)
        setDisclaimerError('Unable to load terms. Please try again later.')
      } finally {
        setDisclaimerLoading(false)
      }
    }

    loadDisclaimer()
  }, [disclaimerVisible, disclaimerContent, disclaimerLoading])

  const disclaimerHtml = useMemo(() => {
    if (!disclaimerContent) return ''
    return marked.parse(disclaimerContent)
  }, [disclaimerContent])

  const handleContactNavigate = useCallback(() => {
    navigate('/support/contact')
    if (isMobile) {
      setMobileMenuOpen(false)
    }
  }, [isMobile, navigate])

  const handleMenuClick = useCallback(({ key }) => {
    if (key === 'disclaimer') {
      handleDisclaimerOpen()
    } else {
      navigate(key)
      if (isMobile) {
        setMobileMenuOpen(false)
      }
    }
  }, [handleDisclaimerOpen, isMobile, navigate])

  const handleLogout = () => {
    dispatch(logout())
  }

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
      onClick: () => navigate('/profile'),
    },
    {
      key: 'purchases',
      icon: <ShoppingOutlined />,
      label: 'Transactions',
      onClick: () => navigate('/purchases'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
      danger: true,
    },
  ]

  const sidebarContent = useMemo(() => (
    <>
      {/* Logo Section */}
      <div
        style={{
          height: 80,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          padding: collapsed ? '0' : '0 24px',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        {!collapsed ? (
          <Space size={12}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
              }}
            >
              <img
                src="/logo.png"
                alt="Logo"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            </div>
            <div>
              <div style={{ color: '#fff', fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>
                Quantum Husky
              </div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, marginTop: 2 }}>
                AI-Powered Trading Analysis
              </div>
            </div>
          </Space>
        ) : (
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
            }}
          >
            <img
              src="/logo.png"
              alt="Logo"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </div>
        )}
      </div>

      {/* Menu */}
      <Menu
        mode="inline"
        selectedKeys={[activeMenuKey]}
        items={menuItems}
        onClick={handleMenuClick}
        style={{
          borderRight: 0,
          marginTop: 24,
          background: 'transparent',
          fontSize: 15,
        }}
        theme="dark"
      />

      {/* Bottom Section */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '16px',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          background: 'rgba(0,0,0,0.2)',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Button
            type="text"
            icon={<CustomerServiceOutlined />}
            onClick={handleContactNavigate}
            style={{
              width: '100%',
              height: 40,
              color: location.pathname.startsWith('/support/contact') ? '#fff' : 'rgba(255,255,255,0.65)',
              fontSize: 14,
              display: 'flex',
              alignItems: 'center',
              justifyContent: collapsed ? 'center' : 'flex-start',
              gap: collapsed ? 0 : 12,
              borderRadius: 8,
              background: location.pathname.startsWith('/support/contact') ? '#667eea' : 'transparent',
              border: 'none',
              transition: 'all 0.2s',
              padding: collapsed ? 0 : '0 16px',
              boxSizing: 'border-box',
              textAlign: collapsed ? 'center' : 'left',
            }}
            onMouseEnter={(e) => {
              if (!location.pathname.startsWith('/support/contact')) {
                e.currentTarget.style.background = 'rgba(255,255,255,0.05)'
                e.currentTarget.style.color = 'rgba(255,255,255,0.9)'
              }
            }}
            onMouseLeave={(e) => {
              if (!location.pathname.startsWith('/support/contact')) {
                e.currentTarget.style.background = 'transparent'
                e.currentTarget.style.color = 'rgba(255,255,255,0.65)'
              }
            }}
          >
            {!collapsed && 'Contact Us'}
          </Button>

          <Button
            type="text"
            icon={<FileTextOutlined />}
            onClick={handleDisclaimerOpen}
            style={{
              width: '100%',
              height: 40,
              color: 'rgba(255,255,255,0.65)',
              fontSize: 14,
              display: 'flex',
              alignItems: 'center',
              justifyContent: collapsed ? 'center' : 'flex-start',
              gap: collapsed ? 0 : 12,
              borderRadius: 8,
              background: 'transparent',
              border: 'none',
              transition: 'all 0.2s',
              padding: collapsed ? 0 : '0 16px',
              boxSizing: 'border-box',
              textAlign: collapsed ? 'center' : 'left',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.05)'
              e.currentTarget.style.color = 'rgba(255,255,255,0.9)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent'
              e.currentTarget.style.color = 'rgba(255,255,255,0.65)'
            }}
          >
            {!collapsed && 'Legal'}
          </Button>

          {!isMobile && (
            <>
              {/* Divider */}
              <div style={{
                width: '100%',
                height: '1px',
                background: 'rgba(255,255,255,0.06)',
                margin: '8px 0',
              }} />

              {/* Collapse Toggle Button */}
              <div
                onClick={() => setCollapsed((prev) => !prev)}
                title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                style={{
                  width: '32px',
                  height: '32px',
                  margin: '0 auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '6px',
                  background: 'rgba(255,255,255,0.04)',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  userSelect: 'none',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.08)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.04)'
                }}
              >
                {collapsed ? (
                  <MenuUnfoldOutlined style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)' }} />
                ) : (
                  <MenuFoldOutlined style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)' }} />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  ), [activeMenuKey, collapsed, handleContactNavigate, handleDisclaimerOpen, handleMenuClick, isMobile, menuItems])

  const getHeaderTitle = useCallback(() => {
    if (location.pathname.startsWith('/purchases')) {
      return 'Transactions'
    }
    if (location.pathname.startsWith('/profile')) {
      return 'Profile'
    }
    if (location.pathname.startsWith('/support/contact')) {
      return 'Support Center'
    }
    const matchedItem = menuItems.find((item) => {
      if (location.pathname === item.key) {
        return true
      }
      return location.pathname.startsWith(`${item.key}/`)
    })
    return matchedItem?.label || 'Quantum Husky'
  }, [location.pathname, menuItems])

  const contentStyle = useMemo(() => ({
    margin: isTaskCreateRoute ? 0 : (isMobile ? '16px' : '32px'),
    minHeight: 280,
    paddingBottom: isMobile ? 32 : 48,
    background: isTaskCreateRoute ? '#ffffff' : 'transparent',
  }), [isMobile, isTaskCreateRoute])

  return (
    <>
      <Layout style={{ minHeight: '100vh' }}>
      {!isMobile && (
        <Sider
          collapsed={collapsed}
          collapsible={!isMobile}
          trigger={null}
          width={260}
          collapsedWidth={80}
          style={{
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            background: 'linear-gradient(180deg, #1a1f36 0%, #0f1419 100%)',
            boxShadow: '4px 0 24px rgba(0,0,0,0.12)',
          }}
        >
          {sidebarContent}
        </Sider>
      )}

      <Drawer
        placement="left"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        width={280}
        styles={{
          body: { padding: 0, background: '#0f1419' },
          mask: { backdropFilter: 'blur(2px)' },
          header: { display: 'none' },
        }}
        closable={false}
      >
        <div style={{ height: '100%', background: 'linear-gradient(180deg, #1a1f36 0%, #0f1419 100%)', position: 'relative' }}>
          {sidebarContent}
        </div>
      </Drawer>

      <Layout
        style={{
          marginLeft: isMobile ? 0 : (collapsed ? 80 : 260),
          transition: 'margin-left 0.2s ease',
          background: 'transparent',
        }}
      >
        <Header
          style={{
            padding: isMobile ? '0 12px' : '0 32px',
            background: '#fff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: isMobile ? 12 : 16,
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
            position: 'sticky',
            top: 0,
            zIndex: 10,
            height: isMobile ? 58 : 72,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12 }}>
            {isMobile && (
              <Button
                type="text"
                icon={<MenuUnfoldOutlined />}
                onClick={() => setMobileMenuOpen(true)}
                style={{ fontSize: 18, padding: 0, width: 36, height: 36 }}
              />
            )}
            <Title
              level={isMobile ? 4 : 3}
              style={{
                margin: 0,
                color: '#1f2937',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontSize: isMobile ? 17 : undefined,
              }}
            >
              {getHeaderTitle()}
            </Title>
          </div>

          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" trigger={['click']}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: isMobile ? 6 : 8,
                cursor: 'pointer',
                padding: isMobile ? '4px 8px' : '4px 8px',
                borderRadius: isMobile ? 999 : 6,
                transition: 'background 0.2s, border 0.2s',
                maxWidth: isMobile ? 'auto' : '50%',
                border: isMobile ? '1px solid rgba(102,126,234,0.35)' : 'none',
                background: isMobile ? 'rgba(102,126,234,0.08)' : 'transparent',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = isMobile ? 'rgba(102,126,234,0.16)' : '#f3f4f6'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = isMobile ? 'rgba(102,126,234,0.08)' : 'transparent'
              }}
            >
              <Avatar
                size={isMobile ? 26 : 32}
                icon={<UserOutlined />}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  flexShrink: 0,
                  border: isMobile ? '1px solid rgba(255,255,255,0.6)' : 'none',
                }}
              />
              {!isMobile && (
                <span
                  style={{
                    fontSize: '14px',
                    color: '#4b5563',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  title={user?.email || 'user@example.com'}
                >
                  {user?.email || 'user@example.com'}
                </span>
              )}
              <DownOutlined style={{ fontSize: isMobile ? 10 : 12, color: '#6366f1' }} />
            </div>
          </Dropdown>
        </Header>

        <Content style={contentStyle}>
          <Outlet />
        </Content>
      </Layout>
      </Layout>
      <SupportChat />
      <Modal
        open={disclaimerVisible}
        title={
          <Space size={10} align="center" className="reports-modal__title">
            <FileTextOutlined className="reports-modal__title-icon" />
            <span>Terms of Use & Privacy Policy</span>
          </Space>
        }
        onCancel={() => setDisclaimerVisible(false)}
        footer={null}
        width={isMobile ? '100vw' : 720}
        style={isMobile ? { top: 0, padding: 0 } : { top: 20 }}
        wrapClassName="reports-modal"
        centered={!isMobile}
        styles={{
          content: { borderRadius: isMobile ? 0 : 16 },
          body: {
            padding: isMobile ? '16px' : '20px 32px 20px 24px',
            background: isMobile ? '#f8fafc' : '#ffffff',
          },
          header: isMobile
            ? {
                padding: '16px 18px 14px',
              }
            : undefined,
        }}
        destroyOnClose
      >
        <div
          className="terms-modal-content"
          style={{
            maxHeight: isMobile ? 'calc(100vh - 180px)' : '70vh',
            overflowY: 'auto',
          }}
        >
          {disclaimerLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}>
              <Spin size="large" />
              <Typography.Text style={{ marginTop: 12 }} type="secondary">
                Loading terms...
              </Typography.Text>
            </div>
          ) : disclaimerError ? (
            <Alert
              type="error"
              message="Failed to load terms"
              description={
                <Typography.Text>
                  {disclaimerError}{' '}
                  <a href="/docs/terms.md" target="_blank" rel="noopener noreferrer">
                    Open raw file
                  </a>
                </Typography.Text>
              }
              showIcon
            />
          ) : (
            <div
              className="markdown-content"
              dangerouslySetInnerHTML={{ __html: disclaimerHtml }}
            />
          )}
        </div>
      </Modal>
    </>
  )
}

export default MainLayout
